import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  cn,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  useSidebar,
} from '@kognitos/lattice';
import { useIsOnboardingOrInvitationPage } from '@/routes/util';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';

import MenuItem from './MenuItem';
import OrganizationSwitcher from './OrganizationSwitcher';
import SidebarTriggerBar from './SidebarTriggerBar';
import UserDropdown from './UserDropdown';
import WorkspaceSwitcher from './WorkspaceSwitcher';
import { useSidebarHoverOverlay } from './hooks/useSidebarHoverOverlay';
import { getWorkspaceMenuItems } from './util';

export function AppSidebar() {
  const { state } = useSidebar();
  const isOnboardingOrInvitationPage = useIsOnboardingOrInvitationPage();

  const { t } = useTranslation('navigation');
  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const isCollapsed = state === 'collapsed';
  const sidebarRef = useRef<HTMLDivElement>(null);

  const {
    isHoverOverlay,
    handleMouseEnter,
    handleMouseLeave,
    handleBackgroundClick,
    handleTriggerClick,
    handleDropdownOpenChange,
  } = useSidebarHoverOverlay(isCollapsed, sidebarRef);

  return (
    <>
      <Sidebar
        ref={sidebarRef}
        collapsible="icon"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleBackgroundClick}
        data-overlay={isHoverOverlay ? 'true' : undefined}
        className={cn(isHoverOverlay && 'fixed left-0 top-0 z-50 shadow-lg')}
      >
        <SidebarHeader className="items-start gap-4">
          <SidebarTriggerBar
            isCollapsed={isCollapsed}
            isHoverOverlay={isHoverOverlay}
            onTriggerClick={handleTriggerClick}
          />
        </SidebarHeader>

        {!isOnboardingOrInvitationPage && (
          <SidebarContent data-testid="app-sidebar-content">
            <SidebarGroup>
              <SidebarGroupLabel className="group-data-[collapsible=icon]:!mt-0">
                {t('navigation.workspace')}
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  <WorkspaceSwitcher
                    isCollapsed={isCollapsed}
                    onDropdownOpenChange={handleDropdownOpenChange}
                  />
                  {getWorkspaceMenuItems(
                    currentOrganizationId,
                    currentWorkspaceId,
                  ).map(item => (
                    <MenuItem
                      key={item.title}
                      item={item}
                      isCollapsed={isCollapsed}
                      onDropdownOpenChange={handleDropdownOpenChange}
                    />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>
        )}

        <SidebarFooter className="p-0 group-data-[collapsible=icon]:pl-2 group-data-[collapsible=icon]:pb-2 transition-none">
          <UserDropdown onDropdownOpenChange={handleDropdownOpenChange} />
        </SidebarFooter>
      </Sidebar>

      <OrganizationSwitcher />
    </>
  );
}
